import * as d3Shape from "d3-shape";
import { scaleLinear } from "d3-scale";
import { interpolateRgb } from "d3-interpolate";

const Gauge = ({ value = 125, min = 0, max = 300 }) => {
  const size = 250;
  const radius = size / 2;
  const thickness = 70;
  const steps = 300;

  const segment1 = 160; // 130
  const segment2 = 200; // 150
  const angle1 = 230; // 180
  const targetAngle = 250; // 200
  const angle2 = 270; // 220

  const blue = "#93C2E4"; // #93C2E4 HMI  , #4a90e2 - mine

  const angleScale = scaleLinear()
    .domain([min, max])
    .range([-Math.PI / 2, Math.PI / 2]);

  const colorScaleGreyToBlue = scaleLinear()
    .domain([0, 1])
    .range(["#ccc", blue])
    .interpolate(interpolateRgb);

  const colorScaleBlueToGrey = scaleLinear()
    .domain([0, 1])
    .range([blue, "#ccc"])
    .interpolate(interpolateRgb);

  const arcs = [];

  // longer ticks

  const ticks = [];
  const labelFontSize = 12;

  for (let i = min; i <= max; i += 50) {
    const angle = angleScale(i);

    const tickRadiusInner = radius - thickness + 100; // outside arc
    const tickRadiusOuter = radius - thickness + 80; // longer ticks outward
    const labelRadius = radius - thickness + 115; // push labels further out

    const x1 = radius + Math.sin(angle) * tickRadiusInner;
    const y1 = radius - Math.cos(angle) * tickRadiusInner;

    const x2 = radius + Math.sin(angle) * tickRadiusOuter;
    const y2 = radius - Math.cos(angle) * tickRadiusOuter;

    const labelX = radius + Math.sin(angle) * labelRadius;
    const labelY = radius - Math.cos(angle) * labelRadius;

    ticks.push(
      <line
        key={`tick-${i}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#555"
        strokeWidth="2"
      />,
      <text
        key={`label-${i}`}
        x={labelX}
        y={labelY}
        textAnchor="middle"
        alignmentBaseline="middle"
        fontSize={labelFontSize}
        fill="#333"
        fontFamily="Segoe UI, sans-serif"
      >
        {i}
      </text>
    );
  }

  // shorter ticks
  for (let i = min; i <= max; i += 10) {
    if (i % 50 === 0) continue; // Skip already labeled major ticks

    const angle = angleScale(i);

    const tickRadiusInner = radius - thickness + 100; // slightly shorter
    const tickRadiusOuter = radius - thickness + 90;

    const x1 = radius + Math.sin(angle) * tickRadiusInner;
    const y1 = radius - Math.cos(angle) * tickRadiusInner;

    const x2 = radius + Math.sin(angle) * tickRadiusOuter;
    const y2 = radius - Math.cos(angle) * tickRadiusOuter;

    ticks.push(
      <line
        key={`minor-tick-${i}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#999"
        strokeWidth="1"
      />
    );
  }

  // Target text
  const targetAngleRad = angleScale(targetAngle);
  const targetLabelRadius = radius - thickness + 155; // push further out than tick labels
  const targetX = radius + Math.sin(targetAngleRad) * targetLabelRadius;
  const targetY = radius - Math.cos(targetAngleRad) * targetLabelRadius;

  // Segment 1: 0 → 20
  arcs.push(
    <path
      key="seg1"
      d={d3Shape
        .arc()
        .innerRadius(radius - thickness)
        .outerRadius(radius)
        .startAngle(angleScale(0))
        .endAngle(angleScale(segment1))()}
      fill="#c8c8c8"
      transform={`translate(${radius}, ${radius})`}
    />
  );

  // Segment 2: 20 → 40
  arcs.push(
    <path
      key="seg2"
      d={d3Shape
        .arc()
        .innerRadius(radius - thickness)
        .outerRadius(radius)
        .startAngle(angleScale(segment1))
        .endAngle(angleScale(segment2))()}
      fill="#d6d6d6"
      transform={`translate(${radius}, ${radius})`}
    />
  );

  // Segment 3: 40 → 180
  arcs.push(
    <path
      key="seg3"
      d={d3Shape
        .arc()
        .innerRadius(radius - thickness)
        .outerRadius(radius)
        .startAngle(angleScale(segment2))
        .endAngle(angleScale(angle1))()}
      fill="#e2e2e2"
      transform={`translate(${radius}, ${radius})`}
    />
  );

  // Segment 4: 180 → 220 (blue)
  arcs.push(
    <path
      key="seg4"
      d={d3Shape
        .arc()
        .innerRadius(radius - thickness)
        .outerRadius(radius)
        .startAngle(angleScale(angle1))
        .endAngle(angleScale(angle2))()}
      fill={blue}
      transform={`translate(${radius}, ${radius})`}
    />
  );

  // Segment 5: 220 → 250
  arcs.push(
    <path
      key="seg5"
      d={d3Shape
        .arc()
        .innerRadius(radius - thickness)
        .outerRadius(radius)
        .startAngle(angleScale(angle2))
        .endAngle(angleScale(max))()}
      fill="#e2e2e2"
      transform={`translate(${radius}, ${radius})`}
    />
  );

  arcs.push(
    <path
      key="arc-outline"
      d={d3Shape
        .arc()
        .innerRadius(radius - thickness) // same inner radius
        .outerRadius(radius + 0.5) // slightly larger outer radius to sit just outside
        .startAngle(angleScale(min))
        .endAngle(angleScale(max))()}
      fill="none"
      stroke="#999999"
      strokeWidth="0.5"
      transform={`translate(${radius}, ${radius})`}
    />
  );

  const needleAngle = angleScale(value);
  const needleLength = radius - thickness / 2 + 20;

  const needleX = radius + Math.sin(needleAngle) * needleLength;
  const needleY = radius - Math.cos(needleAngle) * needleLength;

  const baseWidth = 4; // width of the thick base
  const angleOffset = 0.02; // small spread angle for the base

  const needleBaseLeftX =
    radius + Math.sin(needleAngle - Math.PI / 2) * baseWidth;
  const needleBaseLeftY =
    radius - Math.cos(needleAngle - Math.PI / 2) * baseWidth;

  const needleBaseRightX =
    radius + Math.sin(needleAngle + Math.PI / 2) * baseWidth;
  const needleBaseRightY =
    radius - Math.cos(needleAngle + Math.PI / 2) * baseWidth;

  const tipX = radius + Math.sin(needleAngle) * needleLength;
  const tipY = radius - Math.cos(needleAngle) * needleLength;

  const offset = 120;

  return (
    <svg width={size + offset} height={size}>
      <g
        transform={`translate(${(size + offset) / 2 - radius}, ${
          (size + offset) / 2 - radius + 20
        })`}
      >
        {arcs}
        {ticks}
        <polygon
          points={`${needleBaseLeftX},${needleBaseLeftY} ${needleBaseRightX},${needleBaseRightY} ${tipX},${tipY}`}
          fill="#2e3b5e"
        />

        {/* <text   // TODO: target text 
        x={targetX}
        y={targetY}
        textAnchor="middle"
        fontSize="14"
        fill="#93C2E4"
        fontFamily="Segoe UI, sans-serif"
        fontWeight="bold"
        transform={`rotate(${(targetAngleRad * 180) / Math.PI - 90}, ${targetX}, ${targetY})`}

      >
        Target
      </text> */}
      </g>

      <text
        x={(size + offset) / 2}
        y={size - 20} // below the gauge
        textAnchor="middle"
        fontSize="18"
        fill="#2c2c2c"
        fontFamily="Segoe UI, sans-serif"
      >
        Fill Rate: {value} bpm
      </text>
    </svg>
  );
};

export default Gauge;
